import { addDays, format } from 'date-fns'
import omit from 'lodash-es/omit'

import APPLICANT_DETAILS_SNAPSHOT from '../../../../rental-portal-mock-data/applicant-details.json'
import APPLICANTS_SNAPSHOT from '../../../../rental-portal-mock-data/applicants.json'
import USERS_SNAPSHOT from '../../../../rental-portal-mock-data/users.json'
import VIEWINGS_SNAPSHOT from '../../../../rental-portal-mock-data/viewings.json'
import {
  HTTP_STATUS_CONFLICT,
  HTTP_STATUS_INTERNAL_SERVER_ERROR,
  HTTP_STATUS_NOT_FOUND,
  HTTP_STATUS_UNPROCESSABLE_ENTITY,
} from '../../../constants/api'
import { EMPTY_LIST, EMPTY_OBJECT } from '../../../constants/common'
import { DATE_API_DATA_FORMAT_SHORT } from '../../../constants/date'
import {
  RENTAL_PORTAL_MOCK_LATENCY,
  RENTAL_PORTAL_MOCK_STATE,
  RENTAL_PORTAL_MOCK_STATE_PREFIX,
} from '../../../constants/rentalPortalMockState'
import { MANAGER_USER_ROLE } from '../../../constants/user'
import {
  getRentalPortalMockMediaUrl,
  toRentalPortalMockMediaUser,
} from '../../../utils/rentalPortalMockMedia'
import {
  getRentalPortalMockStateValue,
  hasRentalPortalMockState,
} from '../../../utils/rentalPortalMockState'
import { MOCK_LISTINGS, toMockSessionUser } from '../../RentalPortalListing'
import {
  APPLICANT_COMPARISON_STAGES,
  APPLICANT_CREDIT_CHECK_BAND,
  APPLICANT_STAGE,
} from '../RentalPortalApplicant.constants'
import { getIsRentalPortalApplicantIdentityMasked } from '../RentalPortalApplicantIdentityCell'

import { getMockApplicantHandoffState } from './RentalPortalApplicantCompliance.mocks'
import { getMockApplicantEmailCount } from './RentalPortalApplicantEmails.mocks'
import { getMockApplicantMatch } from './RentalPortalApplicantMatch.mocks'
import {
  getCreatedMockApplicants,
  toServedMockApplicant,
} from './RentalPortalListingApplicants.mocks'

const MOCK_APPLICANT_DETAIL_READ_LATENCY = 300

const MOCK_APPLICANT_DETAIL_READ_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

const MOCK_APPLICANT_DETAIL_NOT_FOUND_ERROR = {
  response: { status: HTTP_STATUS_NOT_FOUND },
}

const MOCK_APPLICANT_DETAIL_WRITE_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

const MOCK_APPLICANT_DETAIL_CONFLICT_ERROR = {
  response: { status: HTTP_STATUS_CONFLICT },
}

const MOCK_APPLICANT_CONFLICTED_IDS = new Set()

function getIsMockApplicantWriteConflict(id) {
  if (
    !hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveConflict) ||
    MOCK_APPLICANT_CONFLICTED_IDS.has(id)
  ) {
    return false
  }

  MOCK_APPLICANT_CONFLICTED_IDS.add(id)

  return true
}

const MOCK_APPLICANT_DETAIL_BY_ID = new Map(
  APPLICANT_DETAILS_SNAPSHOT.records.map(record => [record.id, record])
)

function getMockApplicantDetailRecord(id) {
  return (
    MOCK_APPLICANT_DETAIL_BY_ID.get(id) ??
    APPLICANT_DETAILS_SNAPSHOT.created_applicant_record
  )
}

const MOCK_APPLICANT_USER_BY_ID = new Map(
  USERS_SNAPSHOT.map(user => [user.id, toRentalPortalMockMediaUser(user)])
)

function getMockApplicantUser(userId) {
  return MOCK_APPLICANT_USER_BY_ID.get(userId) ?? null
}

function getMockApplicantPolicy() {
  const isSimpleTenant = hasRentalPortalMockState(
    RENTAL_PORTAL_MOCK_STATE.simpleTenant
  )

  return {
    erp_handoff_enabled:
      !isSimpleTenant &&
      !hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noHandoff),
    four_eyes_required: !hasRentalPortalMockState(
      RENTAL_PORTAL_MOCK_STATE.noFourEyes
    ),
    sanctions_screening_required:
      !isSimpleTenant &&
      !hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noSanctions),
  }
}

const MOCK_APPLICANT_ELIGIBLE_REVIEWER_IDS = [78, 79, 82]

function getMockApplicantReviewers({ policy, selectedByUserId }) {
  if (!policy.four_eyes_required) {
    return { eligible_reviewer_count: null, eligible_reviewers: EMPTY_LIST }
  }

  if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noEligibleReviewer)) {
    return { eligible_reviewer_count: 0, eligible_reviewers: EMPTY_LIST }
  }

  const reviewers = MOCK_APPLICANT_ELIGIBLE_REVIEWER_IDS.filter(
    id => id !== selectedByUserId
  ).map(getMockApplicantUser)

  return {
    eligible_reviewer_count: reviewers.length,
    eligible_reviewers: reviewers,
  }
}

function getMockApplicantDetailPermissions(permissions) {
  const canManage = permissions.can_manage
  const hasAllRights = hasRentalPortalMockState(
    RENTAL_PORTAL_MOCK_STATE.allRights
  )

  return {
    ...permissions,
    can_approve: permissions.can_approve || (canManage && hasAllRights),
    can_configure_compliance_policy:
      hasAllRights ||
      hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.canConfigurePolicy),
    can_edit: canManage,
    can_edit_content: canManage,
    can_edit_responsible_manager: canManage,
    can_reject: canManage,
    can_reveal_identity:
      permissions.can_reveal_identity || (canManage && hasAllRights),
    can_screen:
      canManage && !hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noScreen),
    can_select:
      canManage && !hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noSelect),
    can_view_audit:
      hasAllRights ||
      hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.canViewAudit),
    can_view_sensitive:
      hasAllRights ||
      hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.canViewSensitive),
    can_view_updates: true,
  }
}

const MOCK_APPLICANT_MASKED_FIELD = { masked: true, value: null }

const MOCK_APPLICANT_CREDIT_CHECK_HISTORY_FIELD = 'history'

function toServedMockCreditCheckResult(result, canViewSensitive) {
  return canViewSensitive
    ? result
    : {
        ...result,
        native_value: MOCK_APPLICANT_MASKED_FIELD,
        normalised_band: MOCK_APPLICANT_MASKED_FIELD,
      }
}

function getMockCreditCheckCase(record) {
  const forcedCase = hasRentalPortalMockState(
    RENTAL_PORTAL_MOCK_STATE.creditCheckError
  )
    ? null
    : getRentalPortalMockStateValue(RENTAL_PORTAL_MOCK_STATE_PREFIX.creditCheck)
  const caseKey = forcedCase ?? record.credit_check_case
  const source = APPLICANT_DETAILS_SNAPSHOT.credit_check_cases[caseKey]

  if (!source) {
    console.warn(
      `Unknown rental-portal credit-check mock case "${caseKey}" — serving the fixture's own case instead.`
    )
  }

  return (
    source ??
    APPLICANT_DETAILS_SNAPSHOT.credit_check_cases[record.credit_check_case]
  )
}

const MOCK_APPLICANT_CREDIT_CHECK_REQUESTED = 'requested'

const MOCK_APPLICANT_CREDIT_CHECK_DOCUMENT_ATTACHED = 'document_attached'

const MOCK_APPLICANT_CREDIT_CHECK_CONSENT_TEXT_VERSION = 'agg-2026-05'

const MOCK_APPLICANT_CREDIT_CHECK_REMINDER_MODE = 'reminder'

const MOCK_APPLICANT_CREDIT_CHECK_ATTACHED_RESULT =
  APPLICANT_DETAILS_SNAPSHOT.credit_check_cases.document_attached.expected

const MOCK_APPLICANT_CREDIT_CHECK_BY_ID = new Map()

function getMockApplicantCreditCheckSource({ applicantId, record }) {
  return {
    ...getMockCreditCheckCase(record),
    ...MOCK_APPLICANT_CREDIT_CHECK_BY_ID.get(applicantId),
  }
}

function getMockApplicantCreditCheck({
  applicantId,
  canViewSensitive,
  record,
  stage,
}) {
  const source = getMockApplicantCreditCheckSource({ applicantId, record })

  return {
    ...omit(source, MOCK_APPLICANT_CREDIT_CHECK_HISTORY_FIELD),
    expected: source.expected
      ? toServedMockCreditCheckResult(source.expected, canViewSensitive)
      : null,
    suggestions:
      stage === APPLICANT_STAGE.pool ? EMPTY_LIST : source.suggestions,
    supplementary: source.supplementary.map(result =>
      toServedMockCreditCheckResult(result, canViewSensitive)
    ),
  }
}

const MOCK_APPLICANT_PHASE_KEYS = [
  APPLICANT_STAGE.pool,
  APPLICANT_STAGE.screening,
  APPLICANT_STAGE.selected,
  APPLICANT_STAGE.approved,
]

const MOCK_APPLICANT_PHASE_LABEL = {
  [APPLICANT_STAGE.approved]: 'Approved',
  [APPLICANT_STAGE.pool]: 'Pool',
  [APPLICANT_STAGE.screening]: 'Screening',
  [APPLICANT_STAGE.selected]: 'Selected',
}

const MOCK_APPLICANT_PHASE_STATE = {
  active: 'active',
  done: 'done',
  upcoming: 'upcoming',
}

const MOCK_APPLICANT_PHASE_LABEL_FIELD = 'label'

const MOCK_APPLICANT_STRIP_ITEM_STATE = {
  needsYou: 'needs_you',
  open: 'open',
}

function getServedMockStripItemState({ item, primaryAction }) {
  return item.state === MOCK_APPLICANT_STRIP_ITEM_STATE.needsYou &&
    !primaryAction
    ? MOCK_APPLICANT_STRIP_ITEM_STATE.open
    : item.state
}

const MOCK_APPLICANT_STRIP_META_COUNTS = ['required', 'verified']

function getServedMockStripItemMeta({ documents, meta }) {
  return meta
    ? MOCK_APPLICANT_STRIP_META_COUNTS.reduce(
        (value, count) => value.replace(`{{${count}}}`, documents[count]),
        meta
      )
    : meta
}

function toServedMockStripItem({ documents, item, primaryAction }) {
  return {
    ...item,
    meta: getServedMockStripItemMeta({ documents, meta: item.meta }),
    state: getServedMockStripItemState({ item, primaryAction }),
  }
}

function getMockApplicantPhaseKeys() {
  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.threePhases)
    ? MOCK_APPLICANT_PHASE_KEYS.slice(0, 3)
    : MOCK_APPLICANT_PHASE_KEYS
}

function getMockApplicantActivePhaseIndex(keys, phaseKey) {
  const index = keys.indexOf(phaseKey)

  return index === -1 ? keys.length - 1 : index
}

function getMockApplicantPhaseState({ activeIndex, index, isTerminal }) {
  if (index < activeIndex) {
    return MOCK_APPLICANT_PHASE_STATE.done
  }

  if (index > activeIndex) {
    return MOCK_APPLICANT_PHASE_STATE.upcoming
  }

  return isTerminal
    ? MOCK_APPLICANT_PHASE_STATE.done
    : MOCK_APPLICANT_PHASE_STATE.active
}

function getMockApplicantStripItems(items) {
  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noStripLabel)
    ? items.map((item, index) =>
        index === 0 ? omit(item, MOCK_APPLICANT_PHASE_LABEL_FIELD) : item
      )
    : items
}

function getMockApplicantHasMissingStrip(state) {
  return (
    state === MOCK_APPLICANT_PHASE_STATE.active &&
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noStrip)
  )
}

function getMockApplicantApprovedStripItems({ isHandedOff, items, policy }) {
  return policy.erp_handoff_enabled
    ? [
        ...items,
        isHandedOff
          ? APPLICANT_DETAILS_SNAPSHOT.handoff_strip_item_done
          : APPLICANT_DETAILS_SNAPSHOT.handoff_strip_item,
      ]
    : items
}

function getMockApplicantPhases({
  attention,
  documents,
  isHandedOff,
  policy,
  process,
}) {
  const keys = getMockApplicantPhaseKeys()
  const activeIndex = getMockApplicantActivePhaseIndex(keys, process.phase_key)
  const isTerminal = Boolean(process.terminal_reason)

  return keys.map((key, index) => {
    const state = getMockApplicantPhaseState({ activeIndex, index, isTerminal })
    const items = process.strips[key] ?? EMPTY_LIST
    const primaryAction =
      state === MOCK_APPLICANT_PHASE_STATE.active
        ? (attention?.primary_action ?? null)
        : null

    return {
      key,
      label: MOCK_APPLICANT_PHASE_LABEL[key],
      state,
      strip: getMockApplicantHasMissingStrip(state)
        ? null
        : {
            items: getMockApplicantStripItems(
              (key === APPLICANT_STAGE.approved
                ? getMockApplicantApprovedStripItems({
                    isHandedOff,
                    items,
                    policy,
                  })
                : items
              ).map(item =>
                toServedMockStripItem({ documents, item, primaryAction })
              )
            ),
            primary_action: primaryAction,
          },
    }
  })
}

function getMockApplicantPhase(process) {
  const keys = getMockApplicantPhaseKeys()
  const activeIndex = getMockApplicantActivePhaseIndex(keys, process.phase_key)
  const isTerminal = Boolean(process.terminal_reason)

  return {
    index: activeIndex + 1,
    is_terminal: isTerminal,
    key: keys[activeIndex],
    label: MOCK_APPLICANT_PHASE_LABEL[keys[activeIndex]],
    of: keys.length,
    state: getMockApplicantPhaseState({
      activeIndex,
      index: activeIndex,
      isTerminal,
    }),
    terminal_reason: process.terminal_reason,
  }
}

function getMockApplicantProcess(record, storedDecision) {
  const process =
    APPLICANT_DETAILS_SNAPSHOT.process_cases[
      storedDecision?.process_case ?? record.process_case
    ]

  return storedDecision?.terminal_reason
    ? {
        ...process,
        terminal_at: storedDecision.terminal_at,
        terminal_reason: storedDecision.terminal_reason,
      }
    : process
}

const MOCK_APPLICANT_CHECKLIST_OVERFLOW_ROWS = 18

const MOCK_APPLICANT_CHECKLIST_OVERFLOW_LABEL = 'Sonstiger Nachweis'

let mockApplicantOverflowChecklistRows = null

function getMockApplicantOverflowChecklistRows() {
  mockApplicantOverflowChecklistRows ??= Array.from(
    { length: MOCK_APPLICANT_CHECKLIST_OVERFLOW_ROWS },
    (_, index) => ({
      file: null,
      key: `other_evidence_${index + 1}`,
      rejected_at: null,
      rejected_by_user_id: null,
      rejected_reason: null,
      status: 'missing',
      type_label: `${MOCK_APPLICANT_CHECKLIST_OVERFLOW_LABEL} ${index + 1}`,
      verified_at: null,
      verified_by_user_id: null,
    })
  )

  return mockApplicantOverflowChecklistRows
}

function toServedMockChecklistFile(file) {
  return file
    ? {
        ...omit(file, 'uploaded_by_user_id'),
        size: file.size_bytes,
        uploaded_by: getMockApplicantUser(file.uploaded_by_user_id),
        url: getRentalPortalMockMediaUrl(file.url),
      }
    : null
}

function toServedMockChecklistRow(row) {
  return {
    ...omit(row, 'rejected_by_user_id', 'verified_by_user_id'),
    file: toServedMockChecklistFile(row.file),
    rejected_by: getMockApplicantUser(row.rejected_by_user_id),
    verified_by: getMockApplicantUser(row.verified_by_user_id),
  }
}

const MOCK_APPLICANT_CHECKLIST_STATUS = {
  missing: 'missing',
  rejected: 'rejected',
  uploaded: 'uploaded',
  verified: 'verified',
}

const MOCK_APPLICANT_CHECKLIST_VERDICT_BY_ID = new Map()

const MOCK_APPLICANT_SESSION_USER_ID = 77

function getMockApplicantChecklistVerdicts(applicantId) {
  return MOCK_APPLICANT_CHECKLIST_VERDICT_BY_ID.get(Number(applicantId))
}

function toVerdictedMockChecklistRow({ row, verdicts }) {
  const verdict = verdicts?.get(row.key)

  return verdict ? { ...row, ...verdict } : row
}

const MOCK_APPLICANT_CREDIT_REPORT_CHECKLIST_KEY = 'credit_report'

const MOCK_APPLICANT_CREDIT_REPORT_FILE =
  APPLICANT_DETAILS_SNAPSHOT.documents_cases.credit_report_only.checklist.find(
    row => row.key === MOCK_APPLICANT_CREDIT_REPORT_CHECKLIST_KEY
  ).file

function getMockApplicantCreditReportDocumentId(applicantId) {
  return (
    MOCK_APPLICANT_CREDIT_CHECK_BY_ID.get(Number(applicantId))?.expected
      ?.document_id ?? null
  )
}

function toAttachedMockChecklistRow({ documentId, row }) {
  return documentId &&
    row.key === MOCK_APPLICANT_CREDIT_REPORT_CHECKLIST_KEY &&
    row.status === MOCK_APPLICANT_CHECKLIST_STATUS.missing
    ? {
        ...row,
        file: { ...MOCK_APPLICANT_CREDIT_REPORT_FILE, id: documentId },
        status: MOCK_APPLICANT_CHECKLIST_STATUS.uploaded,
      }
    : row
}

function getMockApplicantChecklistCounts({ documents, rows }) {
  const missing = rows.filter(
    row => row.status === MOCK_APPLICANT_CHECKLIST_STATUS.missing
  )
  const verified = rows.filter(
    row => row.status === MOCK_APPLICANT_CHECKLIST_STATUS.verified
  ).length

  return {
    ...documents,
    complete: verified === rows.length,
    missing: missing.map(row => row.key),
    required: rows.length,
    uploaded: rows.length - missing.length,
    verified,
  }
}

function getMockApplicantDocuments({ applicantId, record }) {
  const source =
    APPLICANT_DETAILS_SNAPSHOT.documents_cases[record.documents_case]
  const isOverflow = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.bulk)
  const overflowRows = isOverflow ? getMockApplicantOverflowChecklistRows() : []
  const verdicts = getMockApplicantChecklistVerdicts(applicantId)
  const documentId = getMockApplicantCreditReportDocumentId(applicantId)

  const rows = [...source.checklist, ...overflowRows].map(row =>
    toVerdictedMockChecklistRow({
      row: toAttachedMockChecklistRow({ documentId, row }),
      verdicts,
    })
  )

  return {
    checklist: rows.map(toServedMockChecklistRow),
    documents: getMockApplicantChecklistCounts({
      documents: source.documents,
      rows,
    }),
  }
}

function getMockApplicantHandoff(applicantId) {
  const handoff = getMockApplicantHandoffState(applicantId)

  return handoff
    ? { status: handoff.status, updated_at: handoff.updated_at }
    : null
}

const MOCK_APPLICANT_TAB = {
  compliance: 'compliance',
  documents: 'documents',
  emails: 'emails',
  overview: 'overview',
  updates: 'updates',
  viewings: 'viewings',
}

const MOCK_APPLICANT_VIEWING_COUNT_BY_ID = VIEWINGS_SNAPSHOT.reduce(
  (acc, viewing) =>
    viewing.applicant
      ? acc.set(viewing.applicant.id, (acc.get(viewing.applicant.id) ?? 0) + 1)
      : acc,
  new Map()
)

function getMockApplicantCounts({ documents, id, updates }) {
  return {
    documents: documents.uploaded,
    emails: getMockApplicantEmailCount(id),
    updates: updates.length,
    viewings: MOCK_APPLICANT_VIEWING_COUNT_BY_ID.get(id) ?? 0,
  }
}

function getMockApplicantTabs({ counts, permissions, policy }) {
  return [
    { count: null, key: MOCK_APPLICANT_TAB.overview, visible: true },
    { count: counts.emails, key: MOCK_APPLICANT_TAB.emails, visible: true },
    {
      count: counts.documents,
      key: MOCK_APPLICANT_TAB.documents,
      visible: true,
    },
    { count: counts.viewings, key: MOCK_APPLICANT_TAB.viewings, visible: true },
    {
      count: null,
      key: MOCK_APPLICANT_TAB.compliance,
      visible:
        permissions.can_view_audit &&
        (policy.sanctions_screening_required || policy.erp_handoff_enabled),
    },
    {
      count: counts.updates,
      key: MOCK_APPLICANT_TAB.updates,
      visible: permissions.can_view_updates,
    },
  ]
}

const MOCK_APPLICANT_UPDATES_OVERFLOW_ROWS = 34

let mockApplicantOverflowUpdates = null

function getMockApplicantOverflowUpdates(events) {
  mockApplicantOverflowUpdates ??= Array.from(
    { length: MOCK_APPLICANT_UPDATES_OVERFLOW_ROWS },
    (_, index) => ({
      ...events[index % events.length],
      at: format(addDays(new Date(), -index), DATE_API_DATA_FORMAT_SHORT),
      id: 8500 + index,
    })
  )

  return mockApplicantOverflowUpdates
}

const MOCK_APPLICANT_EVENT_SYSTEM_ACTOR = {
  avatar: null,
  avatar_color: null,
  deleted: false,
  id: null,
  is_system: true,
  name: null,
  role: null,
  surname: null,
}

function toServedMockApplicantEventActor(actorId) {
  const actor = actorId == null ? null : getMockApplicantUser(actorId)

  return actor
    ? {
        avatar: actor.avatar_thumb_url,
        avatar_color: actor.avatar_color,
        deleted: actor.is_archived,
        id: actor.id,
        is_system: false,
        name: actor.first_name,
        role: MANAGER_USER_ROLE,
        surname: actor.last_name,
      }
    : MOCK_APPLICANT_EVENT_SYSTEM_ACTOR
}

function toServedMockApplicantEvent(event) {
  return {
    ...omit(event, 'actor_id'),
    actor: event.actor ?? toServedMockApplicantEventActor(event.actor_id),
  }
}

function getMockApplicantUpdates({ applicantId, record }) {
  const events = APPLICANT_DETAILS_SNAPSHOT.updates_cases[record.updates_case]

  return [
    ...(hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.bulk)
      ? getMockApplicantOverflowUpdates(events)
      : events),
    ...(MOCK_APPLICANT_REVEAL_BY_ID.get(applicantId)?.events ?? EMPTY_LIST),
    ...(MOCK_APPLICANT_DECISION_BY_ID.get(applicantId)?.events ?? EMPTY_LIST),
    ...(MOCK_APPLICANT_CREDIT_CHECK_EVENTS_BY_ID.get(applicantId) ??
      EMPTY_LIST),
  ]
}

const MOCK_APPLICANT_FOLLOW_UP_DAYS = {
  future: 30,
  near: 3,
  past: -6,
}

function getMockApplicantFollowUp(record) {
  const days = MOCK_APPLICANT_FOLLOW_UP_DAYS[record.follow_up_case]

  return days == null
    ? null
    : format(addDays(new Date(), days), DATE_API_DATA_FORMAT_SHORT)
}

const MOCK_APPLICANT_MATCHED_LISTING_FIELDS = [
  'available_from',
  'currency',
  'flat',
  'id',
  'rent_price',
  'status',
  'unit_label',
]

function getMockApplicantMatchedListing(applicant) {
  if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noMatchedListing)) {
    return null
  }

  const listing = MOCK_LISTINGS.find(item => item.id === applicant.listing_id)

  return listing
    ? Object.fromEntries(
        MOCK_APPLICANT_MATCHED_LISTING_FIELDS.map(field => [
          field,
          listing[field] ?? null,
        ])
      )
    : null
}

function getMockApplicantContactField({ applicant, field }) {
  return getIsRentalPortalApplicantIdentityMasked(applicant)
    ? MOCK_APPLICANT_MASKED_FIELD
    : { masked: false, value: field?.value ?? null }
}

function getMockApplicantProtectedCharacteristics(canViewSensitive) {
  return canViewSensitive
    ? { masked: false, value: null }
    : MOCK_APPLICANT_MASKED_FIELD
}

const MOCK_APPLICANT_PATCH_BY_ID = new Map()

const MOCK_APPLICANT_RESPONSIBLE_MANAGER_FIELD = 'responsible_manager'

const MOCK_APPLICANT_ARCHIVED_FIELD = 'archived'

function toMockApplicantArchivePatch(patch) {
  return MOCK_APPLICANT_ARCHIVED_FIELD in patch
    ? { archived_at: patch.archived ? new Date().toISOString() : null }
    : null
}

export function getIsMockApplicantArchived(applicant) {
  const storedPatch = MOCK_APPLICANT_PATCH_BY_ID.get(applicant.id)

  return Boolean(
    MOCK_APPLICANT_ARCHIVED_FIELD in (storedPatch ?? EMPTY_OBJECT)
      ? storedPatch[MOCK_APPLICANT_ARCHIVED_FIELD]
      : applicant.archived
  )
}

export function setMockApplicantArchived({ archived, id }) {
  MOCK_APPLICANT_PATCH_BY_ID.set(id, {
    ...MOCK_APPLICANT_PATCH_BY_ID.get(id),
    ...toMockApplicantArchivePatch({ archived }),
    [MOCK_APPLICANT_ARCHIVED_FIELD]: archived,
    updated: new Date().toISOString(),
  })
}

function getMockApplicantArchivePermissions({ isArchived, permissions }) {
  return {
    ...permissions,
    can_archive: !isArchived && permissions.can_manage,
    can_restore: isArchived && permissions.can_manage,
  }
}

const MOCK_APPLICANT_MERGED_PATCH_FIELDS = [
  'email',
  'identity',
  'matching',
  'phone',
]

function toMockApplicantMergedPatch({ current, patch }) {
  return Object.fromEntries(
    MOCK_APPLICANT_MERGED_PATCH_FIELDS.filter(field => field in patch).map(
      field => [field, { ...current[field], ...patch[field] }]
    )
  )
}

function toMockApplicantPatch({ current, patch }) {
  return {
    ...patch,
    ...toMockApplicantArchivePatch(patch),
    ...(MOCK_APPLICANT_RESPONSIBLE_MANAGER_FIELD in patch
      ? {
          [MOCK_APPLICANT_RESPONSIBLE_MANAGER_FIELD]: getMockApplicantUser(
            patch[MOCK_APPLICANT_RESPONSIBLE_MANAGER_FIELD]
          ),
        }
      : null),
    ...toMockApplicantMergedPatch({ current, patch }),
  }
}

function getMockApplicantCurrentEnvelopes(applicant) {
  const record = getMockApplicantDetailRecord(applicant.id)

  return {
    email: getMockApplicantContactField({ applicant, field: record.email }),
    identity: applicant.identity,
    matching: applicant.matching ?? record.matching,
    phone: getMockApplicantContactField({ applicant, field: record.phone }),
    ...MOCK_APPLICANT_PATCH_BY_ID.get(applicant.id),
  }
}

function getMockApplicantsSource() {
  return [...getCreatedMockApplicants(), ...APPLICANTS_SNAPSHOT]
}

const MOCK_APPLICANT_DEGRADED_DETAIL_FIELDS = {
  attention: ['attention'],
  creditCheck: ['credit_check'],
  phase: ['phase', 'phases'],
  tabs: ['tabs'],
}

function getDegradedMockApplicantDetailFields() {
  return [
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noAttention)
      ? MOCK_APPLICANT_DEGRADED_DETAIL_FIELDS.attention
      : EMPTY_LIST,
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noPhase)
      ? MOCK_APPLICANT_DEGRADED_DETAIL_FIELDS.phase
      : EMPTY_LIST,
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.creditCheckError)
      ? MOCK_APPLICANT_DEGRADED_DETAIL_FIELDS.creditCheck
      : EMPTY_LIST,
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noTabs)
      ? MOCK_APPLICANT_DEGRADED_DETAIL_FIELDS.tabs
      : EMPTY_LIST,
  ].flat()
}

const MOCK_APPLICANT_DECISION_BY_ID = new Map()

// TODO: the wire vocabulary of the three decision acts is undeclared; §10 declares
// neither the select payload nor RP-05's approve / reject-approval bodies.
const MOCK_APPLICANT_DECISION_ACTION = {
  approve: 'approve',
  rejectSelection: 'reject_selection',
  select: 'select',
}

const MOCK_APPLICANT_DECISION_PROCESS_CASE = {
  [MOCK_APPLICANT_DECISION_ACTION.approve]: 'approved_pending_handoff',
  [MOCK_APPLICANT_DECISION_ACTION.rejectSelection]:
    'selected_awaiting_reviewer',
  [MOCK_APPLICANT_DECISION_ACTION.select]: 'selected_awaiting_reviewer',
}

const MOCK_APPLICANT_DECISION_STAGE = {
  [MOCK_APPLICANT_DECISION_ACTION.approve]: APPLICANT_STAGE.approved,
  [MOCK_APPLICANT_DECISION_ACTION.rejectSelection]: APPLICANT_STAGE.rejected,
  [MOCK_APPLICANT_DECISION_ACTION.select]: APPLICANT_STAGE.selected,
}

const MOCK_APPLICANT_DECISION_EVENT = {
  [MOCK_APPLICANT_DECISION_ACTION.approve]: {
    text: 'Auswahl freigegeben.',
    type: 'approved',
  },
  [MOCK_APPLICANT_DECISION_ACTION.rejectSelection]: {
    text: 'Freigabe der Auswahl abgelehnt.',
    type: 'rejected',
  },
  [MOCK_APPLICANT_DECISION_ACTION.select]: {
    text: 'Applicant selected.',
    type: 'selected',
  },
}

const MOCK_APPLICANT_DECISION_TERMINAL_REASON = 'rejected'

const MOCK_APPLICANT_DECISION_SEGREGATION_ERROR = {
  response: { status: HTTP_STATUS_UNPROCESSABLE_ENTITY },
}

let nextMockApplicantDecisionEventId = 8600

function toMockApplicantDecisionActor(user) {
  return user
    ? {
        avatar: user.avatar_thumb_url,
        avatar_color: user.avatar_color,
        deleted: false,
        id: user.id,
        is_system: false,
        name: user.full_name,
        role: MANAGER_USER_ROLE,
        surname: null,
      }
    : MOCK_APPLICANT_EVENT_SYSTEM_ACTOR
}

function toMockApplicantDecisionEvent({ action, note, user }) {
  nextMockApplicantDecisionEventId += 1

  const event = MOCK_APPLICANT_DECISION_EVENT[action]

  return {
    actor: toMockApplicantDecisionActor(user),
    at: new Date().toISOString(),
    id: nextMockApplicantDecisionEventId,
    text: note ? `${event.text} ${note}` : event.text,
    type: event.type,
  }
}

function toMockApplicantDecisionFields({ action, at, note, user }) {
  if (action === MOCK_APPLICANT_DECISION_ACTION.select) {
    return {
      is_selected: true,
      selected_at: at,
      selected_by: user,
      selected_by_user_id: user?.id ?? null,
      selection_note: note,
    }
  }

  if (action === MOCK_APPLICANT_DECISION_ACTION.approve) {
    return {
      approval_note: note,
      approved_at: at,
      approved_by: user,
      approved_by_user_id: user?.id ?? null,
    }
  }

  return { rejected_at: at, rejected_by: user, rejection_reason: note }
}

const MOCK_APPLICANT_SUPERSEDED_SELECTION = {
  is_selected: false,
  selected_at: null,
  selected_by: null,
  selected_by_user_id: null,
  selection_note: null,
}

const MOCK_APPLICANT_SUPERSEDED_PROCESS_CASE = 'screening_ready'

function getMockApplicantStoredStage(applicant) {
  return (
    MOCK_APPLICANT_DECISION_BY_ID.get(applicant.id)?.stage ??
    MOCK_APPLICANT_REVEAL_BY_ID.get(applicant.id)?.stage ??
    applicant.stage
  )
}

function setMockApplicantSupersededSelection(applicantId) {
  const stored = MOCK_APPLICANT_DECISION_BY_ID.get(applicantId)

  MOCK_APPLICANT_DECISION_BY_ID.set(applicantId, {
    ...stored,
    decision: { ...stored?.decision, ...MOCK_APPLICANT_SUPERSEDED_SELECTION },
    events: stored?.events ?? EMPTY_LIST,
    process_case: MOCK_APPLICANT_SUPERSEDED_PROCESS_CASE,
    stage: APPLICANT_STAGE.screening,
    terminal_at: null,
    terminal_reason: null,
  })
}

function setMockApplicantListingSelectionExclusivity(applicantId) {
  const listingId = findMockApplicant(applicantId)?.listing_id

  getMockApplicantsSource()
    .filter(
      item =>
        item.listing_id === listingId &&
        item.id !== applicantId &&
        getMockApplicantStoredStage(item) === APPLICANT_STAGE.selected
    )
    .forEach(item => setMockApplicantSupersededSelection(item.id))
}

function setMockApplicantDecision({ action, applicantId, note, user }) {
  const at = new Date().toISOString()
  const isRejection = action === MOCK_APPLICANT_DECISION_ACTION.rejectSelection
  const stored = MOCK_APPLICANT_DECISION_BY_ID.get(applicantId)

  if (action === MOCK_APPLICANT_DECISION_ACTION.select) {
    setMockApplicantListingSelectionExclusivity(applicantId)
  }

  MOCK_APPLICANT_DECISION_BY_ID.set(applicantId, {
    decision: {
      ...stored?.decision,
      ...toMockApplicantDecisionFields({ action, at, note, user }),
    },
    events: [
      ...(stored?.events ?? EMPTY_LIST),
      toMockApplicantDecisionEvent({ action, note, user }),
    ],
    process_case: MOCK_APPLICANT_DECISION_PROCESS_CASE[action],
    stage: MOCK_APPLICANT_DECISION_STAGE[action],
    terminal_at: isRejection ? at : null,
    terminal_reason: isRejection
      ? MOCK_APPLICANT_DECISION_TERMINAL_REASON
      : null,
  })
}

const MOCK_APPLICANT_REVEAL_BY_ID = new Map()

function toMockApplicantReleasedIdentity(applicant) {
  const { email, message, phone } = MOCK_APPLICANT_DETAIL_BY_ID.get(
    applicant.id
  )

  return { email, identity: applicant.identity, message, phone }
}

const MOCK_APPLICANT_RELEASED_IDENTITIES = APPLICANTS_SNAPSHOT.filter(
  applicant =>
    applicant.identity?.masked === false &&
    MOCK_APPLICANT_DETAIL_BY_ID.get(applicant.id)?.message?.masked === false
).map(toMockApplicantReleasedIdentity)

function getMockApplicantReleasedIdentity(applicantId) {
  return MOCK_APPLICANT_RELEASED_IDENTITIES[
    applicantId % MOCK_APPLICANT_RELEASED_IDENTITIES.length
  ]
}

const MOCK_APPLICANT_REVEAL_PROCESS_CASE = 'screening_consent_open'

const MOCK_APPLICANT_REVEAL_TRANSITIONS = [
  APPLICANT_STAGE.selected,
  APPLICANT_STAGE.rejected,
]

const MOCK_APPLICANT_REVEAL_EVENTS = [
  { text: 'Moved to screening.', type: 'moved_to_screening' },
  { text: 'Identity revealed (AGG audit log).', type: 'identity_revealed' },
]

let nextMockApplicantRevealEventId = 8800

function toMockApplicantRevealEvent({ event, user }) {
  nextMockApplicantRevealEventId += 1

  return {
    ...event,
    actor: toMockApplicantDecisionActor(user),
    at: new Date().toISOString(),
    id: nextMockApplicantRevealEventId,
  }
}

function setMockApplicantReveal({ applicantId, user }) {
  const revealed = getMockApplicantReleasedIdentity(applicantId)

  MOCK_APPLICANT_REVEAL_BY_ID.set(applicantId, {
    available_transitions: MOCK_APPLICANT_REVEAL_TRANSITIONS,
    events: MOCK_APPLICANT_REVEAL_EVENTS.map(event =>
      toMockApplicantRevealEvent({ event, user })
    ),
    process_case: MOCK_APPLICANT_REVEAL_PROCESS_CASE,
    revealed,
    stage: APPLICANT_STAGE.screening,
  })

  return revealed
}

function toMockApplicantDecision({ policy, record, storedDecision }) {
  const decision = {
    approval_note: null,
    approved_at: record.approved_at,
    approved_by: getMockApplicantUser(record.approved_by_user_id),
    approved_by_user_id: record.approved_by_user_id,
    is_selected: record.is_selected,
    rejected_at: null,
    rejected_by: null,
    rejection_reason: null,
    selected_at: record.selected_at,
    selected_by: getMockApplicantUser(record.selected_by_user_id),
    selected_by_user_id: record.selected_by_user_id,
    selection_note: null,
    ...storedDecision?.decision,
  }

  return {
    ...decision,
    ...getMockApplicantReviewers({
      policy,
      selectedByUserId: decision.selected_by_user_id,
    }),
  }
}

function getIsMockApplicantSegregationViolation({
  action,
  currentUser,
  decision,
  policy,
}) {
  return Boolean(
    action === MOCK_APPLICANT_DECISION_ACTION.approve &&
    policy.four_eyes_required &&
    currentUser?.id != null &&
    decision.selected_by_user_id === currentUser.id
  )
}

const MOCK_APPLICANT_PRIMARY_ACTION_PERMISSIONS = {
  approve: ['can_approve'],
  move_to_screening: ['can_screen'],
  record_verdict: ['can_screen', 'can_view_sensitive'],
}

function getMockApplicantPrimaryAction({ permissions, record }) {
  const permissionNames =
    MOCK_APPLICANT_PRIMARY_ACTION_PERMISSIONS[record.primary_action?.key]

  return permissionNames?.every(name => permissions[name])
    ? record.primary_action
    : null
}

function toMockApplicantDetail(applicant, currentUser) {
  const served = toServedMockApplicant(applicant, currentUser)
  const record = getMockApplicantDetailRecord(applicant.id)
  const storedDecision = MOCK_APPLICANT_DECISION_BY_ID.get(applicant.id)
  const storedReveal = MOCK_APPLICANT_REVEAL_BY_ID.get(applicant.id)
  const storedVerdict = getMockApplicantStoredCreditCheckVerdict(applicant.id)
  const isArchived = getIsMockApplicantArchived(applicant)
  const permissions = getMockApplicantArchivePermissions({
    isArchived,
    permissions: getMockApplicantDetailPermissions(served.permissions),
  })
  const policy = getMockApplicantPolicy()
  const process = getMockApplicantProcess(
    record,
    storedDecision ?? storedReveal
  )
  const documents = getMockApplicantDocuments({
    applicantId: applicant.id,
    record,
  })
  const updates = getMockApplicantUpdates({ applicantId: applicant.id, record })
  const counts = getMockApplicantCounts({
    documents: documents.documents,
    id: applicant.id,
    updates,
  })
  const attention = {
    ...served.attention,
    primary_action:
      storedDecision || storedReveal || storedVerdict
        ? null
        : getMockApplicantPrimaryAction({ permissions, record }),
    tabs_awaiting_you: process.tabs_awaiting_you,
  }

  return omit(
    {
      ...served,
      ...documents,
      ...toMockApplicantDecision({ policy, record, storedDecision }),
      archived_at: record.archived_at,
      attention,
      available_transitions:
        storedReveal?.available_transitions ?? record.available_transitions,
      content: record.note,
      counts,
      credit_check: getMockApplicantCreditCheck({
        applicantId: applicant.id,
        canViewSensitive: permissions.can_view_sensitive,
        record,
        stage: applicant.stage,
      }),
      email: getMockApplicantContactField({
        applicant,
        field: record.email,
      }),
      follow_up: getMockApplicantFollowUp(record),
      handoff: getMockApplicantHandoff(applicant.id),
      income_proof_status: record.income_proof_status ?? null,
      match: getMockApplicantMatch(applicant),
      matched_listing: getMockApplicantMatchedListing(applicant),
      matching: served.matching ?? record.matching,
      message: record.message,
      permissions,
      phase: getMockApplicantPhase(process),
      phases: getMockApplicantPhases({
        attention,
        documents: documents.documents,
        isHandedOff: applicant.handed_off,
        policy,
        process,
      }),
      phone: getMockApplicantContactField({
        applicant,
        field: record.phone,
      }),
      policy,
      protected_characteristics: getMockApplicantProtectedCharacteristics(
        permissions.can_view_sensitive
      ),
      responsible_manager: getMockApplicantUser(record.responsible_manager_id),
      stage: storedDecision?.stage ?? storedReveal?.stage ?? served.stage,
      tabs: getMockApplicantTabs({ counts, permissions, policy }),
      terminal_at: process.terminal_at,
      ...storedReveal?.revealed,
      ...MOCK_APPLICANT_PATCH_BY_ID.get(applicant.id),
    },
    getDegradedMockApplicantDetailFields()
  )
}

function withMockApplicantDetailLatency(isSlow) {
  return new Promise(resolve => {
    setTimeout(
      resolve,
      isSlow ? RENTAL_PORTAL_MOCK_LATENCY : MOCK_APPLICANT_DETAIL_READ_LATENCY
    )
  })
}

function getIsMockApplicantWriteSlow() {
  return (
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.loading) ||
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)
  )
}

function findMockApplicant(applicantId) {
  return getMockApplicantsSource().find(item => item.id === Number(applicantId))
}

const MOCK_APPLICANT_DETAIL_SERVED_IDS = new Set()

function getIsMockApplicantDetailRefresh(applicantId) {
  const id = Number(applicantId)
  const isRefresh = MOCK_APPLICANT_DETAIL_SERVED_IDS.has(id)

  MOCK_APPLICANT_DETAIL_SERVED_IDS.add(id)

  return isRefresh
}

export function getMockApplicantDetail({ applicantId, currentUser }) {
  const isRefresh = getIsMockApplicantDetailRefresh(applicantId)

  return withMockApplicantDetailLatency(
    hasRentalPortalMockState(
      isRefresh
        ? RENTAL_PORTAL_MOCK_STATE.slowRefresh
        : RENTAL_PORTAL_MOCK_STATE.loading
    )
  ).then(() => {
    if (
      hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error) ||
      (isRefresh &&
        hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.refreshError))
    ) {
      return Promise.reject(MOCK_APPLICANT_DETAIL_READ_ERROR)
    }

    const applicant = findMockApplicant(applicantId)

    return applicant
      ? { data: toMockApplicantDetail(applicant, currentUser) }
      : Promise.reject(MOCK_APPLICANT_DETAIL_NOT_FOUND_ERROR)
  })
}

export function patchMockApplicant(applicantId, patch) {
  return withMockApplicantDetailLatency(getIsMockApplicantWriteSlow()).then(
    () => {
      if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_WRITE_ERROR)
      }

      const applicant = findMockApplicant(applicantId)

      if (!applicant) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_NOT_FOUND_ERROR)
      }

      if (getIsMockApplicantWriteConflict(applicant.id)) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_CONFLICT_ERROR)
      }

      MOCK_APPLICANT_PATCH_BY_ID.set(applicant.id, {
        ...MOCK_APPLICANT_PATCH_BY_ID.get(applicant.id),
        ...toMockApplicantPatch({
          current: getMockApplicantCurrentEnvelopes(applicant),
          patch,
        }),
        updated: new Date().toISOString(),
      })

      return { data: toMockApplicantDetail(applicant) }
    }
  )
}

export function postMockApplicantMoveToScreening({ applicantId, currentUser }) {
  return withMockApplicantDetailLatency(getIsMockApplicantWriteSlow()).then(
    () => {
      if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_WRITE_ERROR)
      }

      const applicant = findMockApplicant(applicantId)

      if (!applicant) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_NOT_FOUND_ERROR)
      }

      if (applicant.stage !== APPLICANT_STAGE.pool) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_CONFLICT_ERROR)
      }

      const isConflict = getIsMockApplicantWriteConflict(applicant.id)

      const { identity } = setMockApplicantReveal({
        applicantId: applicant.id,
        user: isConflict
          ? getMockApplicantUser(MOCK_APPLICANT_ELIGIBLE_REVIEWER_IDS[0])
          : toMockSessionUser(currentUser),
      })

      Object.assign(applicant, { identity, stage: APPLICANT_STAGE.screening })

      return isConflict
        ? Promise.reject(MOCK_APPLICANT_DETAIL_CONFLICT_ERROR)
        : { data: toMockApplicantDetail(applicant, currentUser) }
    }
  )
}

export function postMockApplicantDecision({
  action,
  applicantId,
  currentUser,
  note,
}) {
  return withMockApplicantDetailLatency(getIsMockApplicantWriteSlow()).then(
    () => {
      if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_WRITE_ERROR)
      }

      const applicant = findMockApplicant(applicantId)

      if (!applicant) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_NOT_FOUND_ERROR)
      }

      const policy = getMockApplicantPolicy()
      const decision = toMockApplicantDecision({
        policy,
        record: getMockApplicantDetailRecord(applicant.id),
        storedDecision: MOCK_APPLICANT_DECISION_BY_ID.get(applicant.id),
      })

      if (
        getIsMockApplicantSegregationViolation({
          action,
          currentUser,
          decision,
          policy,
        })
      ) {
        return Promise.reject(MOCK_APPLICANT_DECISION_SEGREGATION_ERROR)
      }

      const isConflict = getIsMockApplicantWriteConflict(applicant.id)

      setMockApplicantDecision({
        action,
        applicantId: applicant.id,
        note,
        user: isConflict
          ? getMockApplicantUser(MOCK_APPLICANT_ELIGIBLE_REVIEWER_IDS[0])
          : toMockSessionUser(currentUser),
      })

      return isConflict
        ? Promise.reject(MOCK_APPLICANT_DETAIL_CONFLICT_ERROR)
        : { data: toMockApplicantDetail(applicant, currentUser) }
    }
  )
}

export function patchMockApplicantDocument({
  applicantId,
  currentUser,
  key,
  rejectedReason,
  status,
}) {
  return withMockApplicantDetailLatency(getIsMockApplicantWriteSlow()).then(
    () => {
      if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_WRITE_ERROR)
      }

      const applicant = findMockApplicant(applicantId)

      if (!applicant) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_NOT_FOUND_ERROR)
      }

      const verdicts =
        MOCK_APPLICANT_CHECKLIST_VERDICT_BY_ID.get(applicant.id) ?? new Map()

      const isVerified = status === MOCK_APPLICANT_CHECKLIST_STATUS.verified

      const recordedAt = new Date().toISOString()

      MOCK_APPLICANT_CHECKLIST_VERDICT_BY_ID.set(
        applicant.id,
        verdicts.set(key, {
          rejected_at: isVerified ? null : recordedAt,
          rejected_by_user_id: isVerified
            ? null
            : MOCK_APPLICANT_SESSION_USER_ID,
          rejected_reason: isVerified ? null : (rejectedReason ?? null),
          status,
          verified_at: isVerified ? recordedAt : null,
          verified_by_user_id: isVerified
            ? MOCK_APPLICANT_SESSION_USER_ID
            : null,
        })
      )

      return { data: toMockApplicantDetail(applicant, currentUser) }
    }
  )
}

export function getMockApplicantTimeline({
  applicant_id: applicantId,
  page,
  page_size: pageSize,
}) {
  return withMockApplicantDetailLatency(
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.loading)
  ).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error)) {
      return Promise.reject(MOCK_APPLICANT_DETAIL_READ_ERROR)
    }

    const applicant = findMockApplicant(applicantId)

    if (!applicant) {
      return Promise.reject(MOCK_APPLICANT_DETAIL_NOT_FOUND_ERROR)
    }

    const events = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)
      ? EMPTY_LIST
      : getMockApplicantUpdates({
          applicantId: applicant.id,
          record: getMockApplicantDetailRecord(applicant.id),
        }).map(toServedMockApplicantEvent)

    const pageCount = Math.ceil(events.length / pageSize)
    const currentPage = Math.min(page, Math.max(pageCount, 1))
    const firstIndex = (currentPage - 1) * pageSize

    return {
      data: {
        pagination: {
          page: currentPage,
          page_count: pageCount,
          total: events.length,
        },
        results: {
          objects: events.slice(firstIndex, firstIndex + pageSize),
        },
      },
    }
  })
}

const MOCK_APPLICANT_COMPARISON_FIELDS = [
  'avatar_color',
  'documents',
  'identity',
  'income_proof_status',
  'is_selected',
  'matching',
  'permissions',
  'protected_characteristics',
  'received',
  'reference',
  'stage',
]

export function getMockApplicantsComparison({ listing_id: listingId }) {
  return withMockApplicantDetailLatency(
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.loading)
  ).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error)) {
      return Promise.reject(MOCK_APPLICANT_DETAIL_READ_ERROR)
    }

    const items = (
      hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)
        ? EMPTY_LIST
        : getMockApplicantsSource()
    )
      .filter(
        applicant =>
          applicant.listing_id === Number(listingId) &&
          !applicant.archived &&
          APPLICANT_COMPARISON_STAGES.includes(
            getMockApplicantStoredStage(applicant)
          )
      )
      .sort(
        (first, second) =>
          first.received.localeCompare(second.received) || first.id - second.id
      )
      .map(applicant => {
        const detail = toMockApplicantDetail(applicant)

        return {
          ...Object.fromEntries(
            MOCK_APPLICANT_COMPARISON_FIELDS.map(field => [
              field,
              detail[field] ?? null,
            ])
          ),
          credit_check: {
            expected: detail.credit_check?.expected ?? null,
            readiness_satisfied_by:
              detail.credit_check?.readiness_satisfied_by ?? null,
          },
          id: applicant.id,
        }
      })

    return { data: { items, meta: { count: items.length } } }
  })
}

export function postMockApplicantCreditCheckRequest({
  applicantId,
  isConsentAcknowledged,
  mode,
}) {
  return withMockApplicantDetailLatency(getIsMockApplicantWriteSlow()).then(
    () => {
      if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_WRITE_ERROR)
      }

      const applicant = findMockApplicant(applicantId)

      if (!applicant) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_NOT_FOUND_ERROR)
      }

      const current = getMockApplicantCreditCheck({
        applicantId: applicant.id,
        canViewSensitive: true,
        record: getMockApplicantDetailRecord(applicant.id),
        stage: applicant.stage,
      })
      const now = new Date().toISOString()

      if (!current.consent?.given && !isConsentAcknowledged) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_WRITE_ERROR)
      }

      MOCK_APPLICANT_CREDIT_CHECK_BY_ID.set(applicant.id, {
        ...MOCK_APPLICANT_CREDIT_CHECK_BY_ID.get(applicant.id),
        ...(current.consent?.given
          ? null
          : {
              consent: {
                ...current.consent,
                consent_text_version:
                  MOCK_APPLICANT_CREDIT_CHECK_CONSENT_TEXT_VERSION,
                given: true,
                given_at: now,
              },
            }),
        ...(mode === MOCK_APPLICANT_CREDIT_CHECK_REMINDER_MODE
          ? { reminded_at: now }
          : {
              requested_at: now,
              status: MOCK_APPLICANT_CREDIT_CHECK_REQUESTED,
            }),
      })

      return { data: toMockApplicantDetail(applicant) }
    }
  )
}

export function postMockApplicantCreditCheckDocument({
  applicantId,
  documentId,
}) {
  return withMockApplicantDetailLatency(getIsMockApplicantWriteSlow()).then(
    () => {
      if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_WRITE_ERROR)
      }

      const applicant = findMockApplicant(applicantId)

      if (!applicant) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_NOT_FOUND_ERROR)
      }

      const current = getMockApplicantCreditCheck({
        applicantId: applicant.id,
        canViewSensitive: true,
        record: getMockApplicantDetailRecord(applicant.id),
        stage: applicant.stage,
      })

      MOCK_APPLICANT_CREDIT_CHECK_BY_ID.set(applicant.id, {
        ...MOCK_APPLICANT_CREDIT_CHECK_BY_ID.get(applicant.id),
        expected: {
          ...(current.expected ?? MOCK_APPLICANT_CREDIT_CHECK_ATTACHED_RESULT),
          document_id: documentId,
        },
        status: MOCK_APPLICANT_CREDIT_CHECK_DOCUMENT_ATTACHED,
      })

      return { data: toMockApplicantDetail(applicant) }
    }
  )
}

const MOCK_APPLICANT_CREDIT_CHECK_RECORDED = 'recorded'

const MOCK_APPLICANT_CREDIT_CHECK_EXPIRED = 'expired'

const MOCK_APPLICANT_CREDIT_CHECK_VERDICT_STATUSES = [
  MOCK_APPLICANT_CREDIT_CHECK_RECORDED,
  MOCK_APPLICANT_CREDIT_CHECK_EXPIRED,
]

function getMockApplicantStoredCreditCheckVerdict(applicantId) {
  return MOCK_APPLICANT_CREDIT_CHECK_VERDICT_STATUSES.includes(
    MOCK_APPLICANT_CREDIT_CHECK_BY_ID.get(applicantId)?.status
  )
}

const MOCK_APPLICANT_CREDIT_CHECK_RESULT_INVALID_ERROR = {
  response: { status: HTTP_STATUS_UNPROCESSABLE_ENTITY },
}

let nextMockCreditCheckResultId = 8500

function getIsMockCreditCheckResultExpired(expiresAt) {
  return Boolean(expiresAt && new Date(expiresAt) < new Date())
}

function toMockCreditCheckRecordedResult({ current, values }) {
  nextMockCreditCheckResultId += 1

  const recordedAt = new Date().toISOString()

  return {
    ...current,
    expires_at: values.expires_at,
    id: nextMockCreditCheckResultId,
    is_expired: getIsMockCreditCheckResultExpired(values.expires_at),
    issued_at: values.issued_at,
    native_value: { masked: false, value: values.native_value },
    normalised_band: { masked: false, value: values.normalised_band },
    recorded_at: recordedAt,
    recorded_by_user_id: MOCK_APPLICANT_SESSION_USER_ID,
    superseded_by_id: null,
    verification_code: values.verification_code,
    verified_at: values.is_verified ? recordedAt : null,
  }
}

function getIsMockCreditCheckResultSatisfying(result) {
  return Boolean(
    result &&
    !result.is_expired &&
    !result.superseded_by_id &&
    result.normalised_band?.value &&
    result.normalised_band.value !== APPLICANT_CREDIT_CHECK_BAND.inconclusive
  )
}

function getMockCreditCheckReadinessSatisfiedBy({ expected, supplementary }) {
  if (getIsMockCreditCheckResultSatisfying(expected)) {
    return expected.provider_key
  }

  return (
    (supplementary ?? EMPTY_LIST).find(getIsMockCreditCheckResultSatisfying)
      ?.provider_key ?? null
  )
}

export function postMockApplicantCreditCheckResult({
  applicantId,
  isCorrection,
  values,
}) {
  return withMockApplicantDetailLatency(getIsMockApplicantWriteSlow()).then(
    () => {
      if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_WRITE_ERROR)
      }

      const applicant = findMockApplicant(applicantId)

      if (!applicant) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_NOT_FOUND_ERROR)
      }

      const source = getMockApplicantCreditCheckSource({
        applicantId: applicant.id,
        record: getMockApplicantDetailRecord(applicant.id),
      })

      if (!source.expected?.document_id) {
        return Promise.reject(MOCK_APPLICANT_CREDIT_CHECK_RESULT_INVALID_ERROR)
      }

      const recorded = toMockCreditCheckRecordedResult({
        current: source.expected,
        values,
      })

      MOCK_APPLICANT_CREDIT_CHECK_BY_ID.set(applicant.id, {
        ...MOCK_APPLICANT_CREDIT_CHECK_BY_ID.get(applicant.id),
        expected: recorded,
        history: isCorrection
          ? [
              ...(source.history ?? EMPTY_LIST),
              { ...source.expected, superseded_by_id: recorded.id },
            ]
          : source.history,
        readiness_satisfied_by: getMockCreditCheckReadinessSatisfiedBy({
          expected: recorded,
          supplementary: source.supplementary,
        }),
        status: recorded.is_expired
          ? MOCK_APPLICANT_CREDIT_CHECK_EXPIRED
          : MOCK_APPLICANT_CREDIT_CHECK_RECORDED,
      })

      return { data: toMockApplicantDetail(applicant) }
    }
  )
}

const MOCK_APPLICANT_CREDIT_CHECK_SUGGESTION_STATUS = {
  accepted: 'accepted',
  dismissed: 'dismissed',
  pending: 'pending',
}

const MOCK_APPLICANT_CREDIT_CHECK_SUGGESTION_INVALID_ERROR = {
  response: { status: HTTP_STATUS_UNPROCESSABLE_ENTITY },
}

const MOCK_APPLICANT_CREDIT_CHECK_EXTRACTION = {
  crif: { native_value: 'Klasse B' },
  ksv1870: { native_value: '287' },
  schufa: { native_value: '96,3 %' },
}

const MOCK_APPLICANT_CREDIT_CHECK_EXTRACTION_BAND = {
  crif: APPLICANT_CREDIT_CHECK_BAND.positive,
  ksv1870: APPLICANT_CREDIT_CHECK_BAND.neutral,
  schufa: APPLICANT_CREDIT_CHECK_BAND.positive,
}

function getMockCreditCheckSuggestionExtraction(suggestion) {
  const values =
    MOCK_APPLICANT_CREDIT_CHECK_EXTRACTION[suggestion.detected_provider_key]

  return suggestion.has_extraction && values
    ? {
        ...values,
        issued_at: format(
          new Date(suggestion.arrived_at),
          DATE_API_DATA_FORMAT_SHORT
        ),
        normalised_band:
          MOCK_APPLICANT_CREDIT_CHECK_EXTRACTION_BAND[
            suggestion.detected_provider_key
          ],
      }
    : null
}

function toMockResolvedCreditCheckSuggestions({
  resolution,
  suggestionId,
  suggestions,
}) {
  return suggestions.map(suggestion =>
    suggestion.id === suggestionId
      ? { ...suggestion, ...resolution }
      : suggestion
  )
}

function findMockCreditCheckSuggestion({ suggestionId, suggestions }) {
  return (
    suggestions.find(
      suggestion =>
        suggestion.id === suggestionId &&
        suggestion.status ===
          MOCK_APPLICANT_CREDIT_CHECK_SUGGESTION_STATUS.pending
    ) ?? null
  )
}

function getMockApplicantCurrentCreditCheck(applicant) {
  return getMockApplicantCreditCheck({
    applicantId: applicant.id,
    canViewSensitive: true,
    record: getMockApplicantDetailRecord(applicant.id),
    stage: applicant.stage,
  })
}

const MOCK_APPLICANT_CREDIT_CHECK_EVENTS_BY_ID = new Map()

let nextMockApplicantCreditCheckEventId = 8700

const MOCK_APPLICANT_CREDIT_CHECK_SUGGESTION_DISMISSED_EVENT = {
  text: 'Credit-report email suggestion dismissed.',
  type: 'credit_check_suggestion_dismissed',
}

function addMockApplicantCreditCheckEvent({ applicantId, event, user }) {
  nextMockApplicantCreditCheckEventId += 1

  MOCK_APPLICANT_CREDIT_CHECK_EVENTS_BY_ID.set(applicantId, [
    ...(MOCK_APPLICANT_CREDIT_CHECK_EVENTS_BY_ID.get(applicantId) ??
      EMPTY_LIST),
    {
      ...event,
      actor: toMockApplicantDecisionActor(user),
      at: new Date().toISOString(),
      id: nextMockApplicantCreditCheckEventId,
    },
  ])
}

export function postMockApplicantCreditCheckSuggestionAccept({
  applicantId,
  suggestionId,
}) {
  return withMockApplicantDetailLatency(getIsMockApplicantWriteSlow()).then(
    () => {
      if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_WRITE_ERROR)
      }

      const applicant = findMockApplicant(applicantId)

      if (!applicant) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_NOT_FOUND_ERROR)
      }

      const current = getMockApplicantCurrentCreditCheck(applicant)
      const suggestion = findMockCreditCheckSuggestion({
        suggestionId,
        suggestions: current.suggestions,
      })

      if (!suggestion) {
        return Promise.reject(
          MOCK_APPLICANT_CREDIT_CHECK_SUGGESTION_INVALID_ERROR
        )
      }

      MOCK_APPLICANT_CREDIT_CHECK_BY_ID.set(applicant.id, {
        ...MOCK_APPLICANT_CREDIT_CHECK_BY_ID.get(applicant.id),
        expected: {
          ...(current.expected ?? MOCK_APPLICANT_CREDIT_CHECK_ATTACHED_RESULT),
          document_id: suggestion.file.id,
        },
        status: MOCK_APPLICANT_CREDIT_CHECK_DOCUMENT_ATTACHED,
        suggestions: toMockResolvedCreditCheckSuggestions({
          resolution: {
            status: MOCK_APPLICANT_CREDIT_CHECK_SUGGESTION_STATUS.accepted,
          },
          suggestionId,
          suggestions: current.suggestions,
        }),
      })

      return {
        data: {
          extraction: getMockCreditCheckSuggestionExtraction(suggestion),
        },
      }
    }
  )
}

export function postMockApplicantCreditCheckSuggestionDismiss({
  applicantId,
  currentUser,
  suggestionId,
}) {
  return withMockApplicantDetailLatency(getIsMockApplicantWriteSlow()).then(
    () => {
      if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_WRITE_ERROR)
      }

      const applicant = findMockApplicant(applicantId)

      if (!applicant) {
        return Promise.reject(MOCK_APPLICANT_DETAIL_NOT_FOUND_ERROR)
      }

      const current = getMockApplicantCurrentCreditCheck(applicant)
      const suggestion = findMockCreditCheckSuggestion({
        suggestionId,
        suggestions: current.suggestions,
      })

      if (!suggestion) {
        return Promise.reject(
          MOCK_APPLICANT_CREDIT_CHECK_SUGGESTION_INVALID_ERROR
        )
      }

      const user = toMockSessionUser(currentUser)

      MOCK_APPLICANT_CREDIT_CHECK_BY_ID.set(applicant.id, {
        ...MOCK_APPLICANT_CREDIT_CHECK_BY_ID.get(applicant.id),
        suggestions: toMockResolvedCreditCheckSuggestions({
          resolution: {
            resolved_at: new Date().toISOString(),
            resolved_by: user,
            resolved_by_user_id: user?.id ?? null,
            status: MOCK_APPLICANT_CREDIT_CHECK_SUGGESTION_STATUS.dismissed,
          },
          suggestionId,
          suggestions: current.suggestions,
        }),
      })

      addMockApplicantCreditCheckEvent({
        applicantId: applicant.id,
        event: MOCK_APPLICANT_CREDIT_CHECK_SUGGESTION_DISMISSED_EVENT,
        user,
      })

      return { data: toMockApplicantDetail(applicant, currentUser) }
    }
  )
}

export function getMockApplicantCreditCheckSuggestions(applicantId) {
  return withMockApplicantDetailLatency(
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.loading)
  ).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error)) {
      return Promise.reject(MOCK_APPLICANT_DETAIL_READ_ERROR)
    }

    const applicant = findMockApplicant(applicantId)

    if (!applicant) {
      return Promise.reject(MOCK_APPLICANT_DETAIL_NOT_FOUND_ERROR)
    }

    return {
      data: {
        items: getMockApplicantCreditCheck({
          applicantId: applicant.id,
          canViewSensitive: false,
          record: getMockApplicantDetailRecord(applicant.id),
          stage: applicant.stage,
        }).suggestions.filter(
          suggestion =>
            suggestion.status ===
            MOCK_APPLICANT_CREDIT_CHECK_SUGGESTION_STATUS.pending
        ),
      },
    }
  })
}

export function getMockApplicantCreditCheckHistory(applicantId) {
  return withMockApplicantDetailLatency(
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.loading)
  ).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error)) {
      return Promise.reject(MOCK_APPLICANT_DETAIL_READ_ERROR)
    }

    const applicant = findMockApplicant(applicantId)

    if (!applicant) {
      return Promise.reject(MOCK_APPLICANT_DETAIL_NOT_FOUND_ERROR)
    }

    const canViewSensitive = getMockApplicantDetailPermissions(
      toServedMockApplicant(applicant).permissions
    ).can_view_sensitive

    return {
      data: {
        items: (
          getMockApplicantCreditCheckSource({
            applicantId: applicant.id,
            record: getMockApplicantDetailRecord(applicant.id),
          }).history ?? EMPTY_LIST
        ).map(result =>
          toServedMockCreditCheckResult(result, canViewSensitive)
        ),
      },
    }
  })
}
